import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { IPopup } from 'app/shared/elements/popup-base';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator } from 'aurelia-event-aggregator';
import { CareDayStatusReasonReloadEvent } from 'app/um/lib/events';
import { localSort } from 'app/shared/lib/sorting';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import * as UM from 'app/um/models/essette-api';

interface CustomCareDayStatus extends UM.AdmissionCareDayStatus {
    // Values For Grid Column Sorting
    transitionValue?: string | null;
    eventValue?: string | null;
    // Display For Grid Column Display
    transitionDisplay?: string | null;
    eventDisplay?: string | null;
    // Tooltip list For Grid Column
    transitionToolTipDesc?: string | null;
}

@autoinject()
export class CareDayStatuses extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected careDayStatuses: CustomCareDayStatus[] = [];
    protected editedCareDayStatus: UM.AdmissionCareDayStatus = {};

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['transitionValue', 'eventValue'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { localSorting, apiSorting } = this;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const careDayStatuses: CustomCareDayStatus[] = <CustomCareDayStatus[]>this.careDayStatuses;
            if(direction === null) {
                localSorting.remove(property);
                // if no local sorts, revert to API sorts by reloading
                if(localSorting.entries.length === 0) this.reload();
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(careDayStatuses, localSorting);
        }
        // Else If API Sort
        else {
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCareDayStatuses();
    }

    protected saveCareDayStatus(careDayStatus: UM.AdmissionCareDayStatus = {}): void {
        this.editedCareDayStatus = deepClone(careDayStatus);
        this.$modal.open();
    }

    protected async handleFormSuccess(): Promise<void> {
        this.$modal.close();
        await this.reload();
    }

    // Set Custom Display
    protected setCareDayStatusDetails = (careDayStatuses: CustomCareDayStatus[], allActiveCount: number = 0): CustomCareDayStatus[] => {
        careDayStatuses.forEach(status => {
            // transform transition data
            const transitions = status.transitions || [];
            if(transitions.length) {
                const transitionCount = transitions.length;
                if(transitionCount === allActiveCount) { // if all statuses are transitions
                    status.transitionValue = status.transitionDisplay = 'All Status Transitions';
                } else if(transitionCount === 1) {
                    status.transitionValue = status.transitionDisplay = transitions[0].description;
                } else {
                    status.transitionDisplay = status.transitionValue = transitionCount.toString() + ' Transitions';
                    let listDisplay = '<ul>';
                    transitions.forEach(t => {
                        listDisplay += '<li>' + t.description + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.transitionToolTipDesc = listDisplay;
                }
            } else {
                status.transitionDisplay = status.transitionValue = "None";
            }

            // transform event data
            if(status.flag?.isInitialStatus) {
                status.eventDisplay = status.eventValue = this.i18n.tr('um:auth.careDays.status.flag.isInitialStatus.$');
            } else {
                status.eventDisplay = status.eventValue = '';
            }
        });

        return careDayStatuses;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateCareDayStatus(authStatus: UM.AdmissionCareDayStatus): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(authStatus.isActive) {
            const result = await confirmation.showDeactivate('um:auth.careDays.status.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCareDayStatus(authStatus.id!, !authStatus.isActive);
            isSuccessful = response.isSuccessful;
            this.events.publish(new CareDayStatusReasonReloadEvent());
            this.reload();

        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.careDays.status.$long', !authStatus.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCareDayStatuses(): Promise<void> {
        const includes: string[] = ['transitions', 'authStatus'];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCareDayStatuses(options, includes);
            const { count: allActiveCount } = await this.service.loadCareDayStatuses({ all: true, includeInactive: false });
            this.careDayStatuses = this.setCareDayStatusDetails(<CustomCareDayStatus[]>response.results, allActiveCount || 0);
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
